import { Container, Graphics, type ColorSource } from "pixi.js";

export interface HealthBarOptions {
  width?: number;
  height?: number;
  color?: ColorSource;
  damageColor?: ColorSource;
  background?: ColorSource;
  yOffset?: number;
}

/**
 * Two-pass health bar: a background rect + foreground rect scaled by ratio.
 * `setRatio(0..1)` redraws only the foreground (cheap).
 */
export class HealthBar extends Container {
  private readonly bg: Graphics;
  private readonly fg: Graphics;
  private readonly w: number;
  private readonly h: number;
  private readonly fgColor: ColorSource;
  private readonly damageColor: ColorSource;
  private ratio = 1;

  constructor(opts: HealthBarOptions = {}) {
    super({ label: "healthbar" });
    this.w = opts.width ?? 40;
    this.h = opts.height ?? 4;
    this.fgColor = opts.color ?? 0x4ade80;
    this.damageColor = opts.damageColor ?? 0xef4444;
    this.bg = new Graphics();
    this.fg = new Graphics();
    this.addChild(this.bg);
    this.addChild(this.fg);
    this.bg
      .rect(-this.w / 2, -this.h / 2, this.w, this.h)
      .fill({ color: opts.background ?? 0x111317, alpha: 0.85 })
      .stroke({ color: 0x000000, width: 1, alpha: 0.5 });
    this.position.set(0, opts.yOffset ?? -32);
    this.redraw();
  }

  setRatio(value: number): void {
    const clamped = value < 0 ? 0 : value > 1 ? 1 : value;
    if (Math.abs(clamped - this.ratio) < 0.001) return;
    this.ratio = clamped;
    this.redraw();
  }

  private redraw(): void {
    this.fg.clear();
    if (this.ratio <= 0) return;
    const fillW = this.w * this.ratio;
    const color = this.ratio > 0.5 ? this.fgColor : this.damageColor;
    this.fg.rect(-this.w / 2, -this.h / 2, fillW, this.h).fill({ color });
  }
}
